import React, { ChangeEvent } from 'react'
import { graphql } from 'react-apollo'

import { Spinner } from '@acorns/web-components'
import { useRemoteStrings } from '@acorns/web-utils'
import { FormikProps, withFormik } from 'formik'
import { compose, fromRenderProps, withHandlers, withProps, withState } from 'recompose'

import AutoCompleteAddress from 'components/AutocompleteAddress'
import { OnAutoCompleteSelect } from 'components/AutocompleteAddress/types'
import Button from 'components/Button'
import { CTAContainer } from 'components/CTAContainer'
import {
  FormItem,
  FormRow,
  StyledFormContainer,
  StyledSelect,
  StyledTextInput,
} from 'components/FormContainer/styled-components'
import withHeroText, { HeroTextProps } from 'components/HOCs/withHeroText'
import withRemoteStrings, { RemoteStringsProps } from 'components/HOCs/withRemoteStrings'
import Layout from 'components/Layout'
import Nav from 'components/Nav'
import Separator from 'components/Separator'
import { withClickTrack } from 'components/Trackers/ClickTracker'
import { TRACKER_IDS } from 'components/Trackers/trackerIDs'
import { PageName } from 'generated/types'
import { TestIDs } from 'test/utils/testIds'
import { withApolloErrorModal } from 'utils/Apollo'
import { withFlowRouter } from 'utils/FlowProvider'
import { FunnelContext, FunnelContextProps } from 'utils/FunnelContext'
import { shouldGrayOutCTACall } from 'utils/shouldGrayOutCTA'
import states from 'utils/states'

import {
  CityField,
  FooterContainer,
  MobileOnlyFootnote,
  StateField,
  StyledHeroHeader,
} from '../styled-components'
import { handleSubmit, mapPropsToValues, validate } from './form'
import mutation from './mutation.gql'
import userQuery from './query.gql'

type InnerProps = {
  handleSelectChange: (field: string, value: string) => void
  onChange: (e: React.SyntheticEvent) => void
  header: string
  text: string
  loading: boolean
} & FormikProps<any> &
  HeroTextProps &
  RemoteStringsProps &
  FunnelContextProps

const SubmitContactInfo = withClickTrack<any, any>(TRACKER_IDS.CONTACT_INFO.NEXT_BUTTON)(Button)

export const enhance = compose<InnerProps, {}>(
  fromRenderProps<Partial<FunnelContextProps>, {}, Partial<FunnelContextProps>>(
    FunnelContext.Consumer,
    ({ isBackEnabled, isDynamicFunnelEnabled, routeToPage }) => ({
      isBackEnabled,
      isDynamicFunnelEnabled,
      routeToPage,
    }),
  ),
  withFlowRouter,
  withApolloErrorModal,
  withHeroText,
  withRemoteStrings,
  withState('submitAttempted', 'setSubmitAttempted', false),
  graphql(mutation, { name: 'updateContactInfo' }),
  graphql(userQuery, { options: { fetchPolicy: 'network-only' } }),
  withFormik({
    enableReinitialize: true,
    mapPropsToValues,
    isInitialValid: true,
    handleSubmit,
    validate,
    validateOnChange: true,
  }),
  withProps(({ getRemoteString, heroText, data }) => {
    return {
      loading: data.loading,
      header: getRemoteString('contact_info_header', heroText.header),
      text: getRemoteString('contact_info_text', heroText.text),
    }
  }),
  withHandlers({
    handleSelectChange: ({ setFieldValue, submitAttempted, validateForm }) => async (
      field,
      value,
    ) => {
      await setFieldValue(field, value)

      // Only validate the form if the user has attempted at least one submit
      if (submitAttempted) {
        validateForm()
      }
    },
    onChange: props => async e => {
      // https://github.com/jaredpalmer/formik/issues/1218#issuecomment-451119016
      await props.handleChange(e)

      // Only validate the form if the user has attempted at least one submit
      if (props.submitAttempted) {
        props.validateForm()
      }
    },
    handleSubmitIntercept: ({
      submitForm,
      submitAttempted,
      setSubmitAttempted,
      validateForm,
    }) => async () => {
      if (!submitAttempted) {
        await setSubmitAttempted(true)
        validateForm()
      }

      submitForm()
    },
  }),
)

export const ContactInfo: React.FC<InnerProps> = props => {
  const { getRemoteString } = useRemoteStrings()
  const stateValues = [...states]
  const { setFieldValue } = props

  const shouldGrayOutCTA = shouldGrayOutCTACall({
    values: props.values,
    validation: values => validate(values, { forGrayOutCTACheck: true, ...props }),
    ignoredKeys: ['address2'],
  })

  const handlePhoneMask = (e: ChangeEvent<HTMLInputElement>) => {
    const element = e
    const x = element.target.value.replace(/\D/g, '').match(/(\d{0,3})(\d{0,3})(\d{0,4})/)
    element.target.value = !x[2] ? x[1] : '(' + x[1] + ') ' + x[2] + (x[3] ? '-' + x[3] : '')
    return props.onChange(element)
  }

  const handleZipcodeMask = (e: ChangeEvent<HTMLInputElement>) => {
    const element = e
    const x = element.target.value.replace(/\D/g, '').match(/(\d{0,5})/)
    element.target.value = x[1]
    return props.onChange(element)
  }

  const handleAutocompleteSelect: OnAutoCompleteSelect = googleAddress => {
    setFieldValue('address1', googleAddress.street1)
    setFieldValue('address2', googleAddress.street2)
    setFieldValue('city', googleAddress.city)
    setFieldValue('state', googleAddress.state)
    setFieldValue('zip', googleAddress.zipCode)
  }

  return (
    <>
      <Nav
        back={props.isDynamicFunnelEnabled && props.isBackEnabled(PageName.ContactInfo)}
        backClickTracker={TRACKER_IDS.CONTACT_INFO.BACK_BUTTON}
      />
      <Layout
        header={props.header}
        text={props.text}
        footnoteLock={props.heroText.footnoteLock}
        footnote={props.heroText.footnote}
      >
        <StyledHeroHeader>{props.heroText.text}</StyledHeroHeader>
        <StyledFormContainer>
          <FormRow>
            <FormItem>
              <AutoCompleteAddress
                {...props}
                name={'address1'}
                id={'address1'}
                type={'text'}
                placeholder={getRemoteString({
                  key: 'registration.contact_info.address.placeholder',
                })}
                value={props.values.address1}
                onChange={e => props.setFieldValue('address1', e.target.value)}
                onAutoCompleteSelect={handleAutocompleteSelect}
              />
            </FormItem>
            <FormItem>
              <StyledTextInput
                label={{
                  value: getRemoteString({
                    key: 'registration.contact_information.address_2.title',
                  }),
                }}
                className="text-input"
                inputProps={{
                  name: 'address2',
                  value: props.values.address2,
                  onChange: props.onChange,
                  id: 'address2',
                  type: 'text',
                  placeholder: getRemoteString({
                    key: 'registration.contact_information.address_2.placeholder',
                  }),
                  autoComplete: 'arbitraryToDisableBrowserProp',
                }}
                error={!!props.errors.address2}
                errorText={props.errors.address2 as string}
                data-testid={TestIDs.CONTACT_INFO.ADDRESS_LINE_2}
              />
            </FormItem>
            <CityField>
              <FormItem>
                <StyledTextInput
                  label={{ value: 'City' }}
                  className="text-input"
                  inputProps={{
                    name: 'city',
                    value: props.values.city,
                    onChange: props.onChange,
                    id: 'city',
                    type: 'text',
                    placeholder: getRemoteString({
                      key: 'registration.contact_information.city.placeholder',
                    }),
                    autoComplete: 'arbitraryToDisableBrowserProp',
                  }}
                  error={!!props.errors.city}
                  errorText={props.errors.city as string}
                  data-testid={TestIDs.CONTACT_INFO.CITY}
                />
              </FormItem>
            </CityField>
            <StateField>
              <FormItem>
                <StyledSelect
                  name="state"
                  id="state"
                  label="State"
                  value={props.values.state}
                  onChange={(field, value) => props.handleSelectChange(field, value)}
                  options={stateValues}
                  placeholder={getRemoteString({
                    key: 'registration.contact_information.state.placeholder',
                  })}
                  error={!!props.errors.state}
                  errorText={props.errors.state as string}
                  data-testid={TestIDs.CONTACT_INFO.STATE}
                />
              </FormItem>
            </StateField>
            <FormItem>
              <StyledTextInput
                label={{
                  value: getRemoteString({ key: 'registration.contact_information.zip.title' }),
                }}
                className="text-input"
                inputProps={{
                  name: 'zip',
                  value: props.values.zip,
                  onChange: handleZipcodeMask,
                  id: 'zip',
                  type: 'text',
                  placeholder: getRemoteString({
                    key: 'registration.contact_information.zip.placeholder',
                  }),
                  inputMode: 'numeric',
                  autoComplete: 'arbitraryToDisableBrowserProp',
                }}
                error={!!props.errors.zip}
                errorText={props.errors.zip as string}
                data-testid={TestIDs.CONTACT_INFO.ZIP_CODE}
              />
            </FormItem>
            <FormItem>
              <StyledTextInput
                label={{
                  value: getRemoteString({
                    key: 'registration.contact_information.phone_number.title',
                  }),
                }}
                className="text-input"
                inputProps={{
                  name: 'phoneNumber',
                  value: props.values.phoneNumber,
                  onChange: handlePhoneMask,
                  id: 'phone',
                  type: 'text',
                  placeholder: getRemoteString({
                    key: 'registration.contact_information.phone_number.placeholder',
                  }),
                  inputMode: 'numeric',
                }}
                helperText="We’ll use this number to protect your account by sending  two-factor authentication verification codes."
                error={!!props.errors.phoneNumber}
                errorText={props.errors.phoneNumber as string}
                data-testid={TestIDs.CONTACT_INFO.PHONE_NUMBER}
              />
            </FormItem>
          </FormRow>
        </StyledFormContainer>
        <Separator mobileCtaSeparator extraCtaMargin />
        <CTAContainer>
          <SubmitContactInfo
            styleType={!props.isValid || shouldGrayOutCTA ? 'invalid' : undefined}
            onPress={props.handleSubmitIntercept}
            data-testid={TestIDs.CONTACT_INFO.SUBMIT_CTA}
            disabled={props.isSubmitting || props.loading}
            isValid={props.isValid}
            buttonType={'submit'}
          >
            {props.isSubmitting ? <Spinner height={20} width={20} white={true} /> : 'Next'}
          </SubmitContactInfo>
          <MobileOnlyFootnote>
            <i>{props.heroText.footerText}</i>
          </MobileOnlyFootnote>
        </CTAContainer>
        <FooterContainer>
          <i>{props.heroText.footerText}</i>
        </FooterContainer>
      </Layout>
    </>
  )
}

export default enhance(ContactInfo)
